import { Card, CardBody, CardHeader, Select, SelectItem } from '@heroui/react'
import { useMemo, useState } from 'react'
import { Area, CartesianGrid, Legend, AreaChart as RechartsAreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { useSchemaTimeSeries } from './utils'

// Color palette for different lines
const COLORS = {
  0: '#3B82F6', // blue-500
  1: '#10B981', // green-500
  2: '#F59E0B', // amber-500
  3: '#EF4444', // red-500
  4: '#8B5CF6', // violet-500
}

/**
 * Formats date strings according to time interval
 */
function formatDate(timestamp, interval) {
  try {
    const date = new Date(timestamp)
    const options = { month: 'short', day: 'numeric' }

    if (interval === 'hourly') {
      return new Intl.DateTimeFormat('en-US', {
        ...options,
        hour: 'numeric',
        hour12: true,
      }).format(date)
    }

    if (interval === 'weekly') {
      return new Intl.DateTimeFormat('en-US', options).format(date)
    }

    // Default daily format
    return new Intl.DateTimeFormat('en-US', options).format(date)
  }
  catch (error) {
    console.error('Error formatting date:', error, timestamp)
    return timestamp
  }
}

export default function ExtractionRateChart({ schemaId, dateRange }) {
  const [selectedVariables, setSelectedVariables] = useState(new Set())
  const [timeInterval, setTimeInterval] = useState('daily')

  const { data, isLoading } = useSchemaTimeSeries(schemaId, dateRange, timeInterval)

  // Transform the time series data for Recharts
  const chartData = useMemo(() => {
    if (!data?.time_series?.length)
      return []

    return data.time_series.map((point) => {
      const dataPoint = {
        timestamp: point.timestamp,
        formattedDate: formatDate(point.timestamp, timeInterval),
      }

      // Add extraction rate for each variable
      point.variables_data?.forEach((variable) => {
        dataPoint[variable.variable_name] = (variable.extraction_rate * 100).toFixed(1)
      })

      return dataPoint
    })
  }, [data, timeInterval])

  // Get all unique variable names
  const allVariables = useMemo(() => {
    const variableSet = new Set()
    data?.time_series?.forEach((point) => {
      point.variables_data?.forEach((variable) => {
        variableSet.add(variable.variable_name)
      })
    })
    return Array.from(variableSet)
  }, [data])

  // Default to showing all variables if none selected
  const displayVariables = selectedVariables.size > 0
    ? Array.from(selectedVariables)
    : allVariables

  if (isLoading) {
    return (
      <Card classNames={{
        base: 'w-full border-2 border-gray-200 shadow-none rounded-none',
      }}
      >
        <CardBody>
          <div className="h-64 animate-pulse bg-gray-100" />
        </CardBody>
      </Card>
    )
  }

  if (!chartData.length) {
    return (
      <Card classNames={{
        base: 'w-full border-2 border-gray-200 shadow-none rounded-none',
      }}
      >
        <CardBody>
          <div className="w-full h-full flex flex-col items-center my-24">
            <h1 className="text-2xl text-gray-400 tracking-tighter font-semibold mb-6">
              No time series data yet.
            </h1>
          </div>
        </CardBody>
      </Card>
    )
  }

  return (
    <Card classNames={{
      base: 'w-full border-2 border-gray-200 shadow-none rounded-none',
    }}
    >
      <CardHeader className="flex flex-col gap-3 border-b border-gray-200">
        <div className="flex items-center justify-start w-full gap-4">
          <h4 className="text-lg font-taurus mr-auto font-semibold">Extraction Performance Over Time</h4>
          <Select
            placeholder="Showing all variables"
            selectionMode="multiple"
            selectedKeys={selectedVariables}
            onSelectionChange={setSelectedVariables}
            className="max-w-xs"
            size="sm"
            variant="bordered"
            radius="none"
            classNames={{
              trigger: 'border-gray-200',
              value: 'font-mono text-sm',
              label: 'text-xs text-gray-500',
            }}
          >
            {allVariables.map(variable => (
              <SelectItem key={variable}>
                {variable}
              </SelectItem>
            ))}
          </Select>
          <Select
            selectedKeys={[timeInterval]}
            onSelectionChange={keys => setTimeInterval(Array.from(keys)[0])}
            className="w-32"
            size="sm"
            variant="bordered"
            radius="none"
            classNames={{
              trigger: 'border-gray-200',
              value: 'font-mono text-sm',
            }}
          >
            <SelectItem key="hourly">Hourly</SelectItem>
            <SelectItem key="daily">Daily</SelectItem>
            <SelectItem key="weekly">Weekly</SelectItem>
          </Select>
        </div>

      </CardHeader>
      <CardBody>
        <ResponsiveContainer width="100%" height={300}>
          <RechartsAreaChart
            data={chartData}
            margin={{
              top: 10,
              right: 30,
              left: 0,
              bottom: 0,
            }}
          >
            <defs>
              {displayVariables.map((variable, index) => (
                <linearGradient key={variable} id={`gradient-${variable}`} x1="0" y1="0" x2="0" y2="1">
                  <stop
                    offset="5%"
                    stopColor={COLORS[index % Object.keys(COLORS).length]}
                    stopOpacity={0.3}
                  />
                  <stop
                    offset="95%"
                    stopColor={COLORS[index % Object.keys(COLORS).length]}
                    stopOpacity={0}
                  />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
            <XAxis
              dataKey="formattedDate"
              tick={{ fontSize: 12, fontFamily: 'ui-monospace, monospace' }}
              interval="preserveStartEnd"
            />
            <YAxis
              domain={[0, 100]}
              tick={{ fontSize: 12, fontFamily: 'ui-monospace, monospace' }}
              tickFormatter={value => `${value}%`}
            />
            <Tooltip
              content={({ active, payload, label }) => {
                if (active && payload && payload.length) {
                  return (
                    <div className="bg-white border-2 border-gray-200 p-3 shadow-none">
                      <p className="text-sm font-mono font-medium mb-2">{label}</p>
                      {payload.map((entry, index) => (
                        <div key={index} className="flex items-center gap-2 text-xs font-mono">
                          <div
                            className="w-3 h-3"
                            style={{ backgroundColor: entry.color }}
                          />
                          <span className="text-gray-500">
                            {entry.name}
                            :
                          </span>
                          <span className="font-medium">
                            {entry.value}
                            %
                          </span>
                        </div>
                      ))}
                    </div>
                  )
                }
                return null
              }}
            />
            <Legend
              wrapperStyle={{
                paddingTop: '20px',
                fontSize: '12px',
                fontFamily: 'ui-monospace, monospace',
              }}
              iconType="line"
            />
            {displayVariables.map((variable, index) => (
              <Area
                key={variable}
                type="monotone"
                dataKey={variable}
                stroke={COLORS[index % Object.keys(COLORS).length]}
                strokeWidth={2}
                fill={`url(#gradient-${variable})`}
                activeDot={{ r: 5 }}
              />
            ))}
          </RechartsAreaChart>
        </ResponsiveContainer>
      </CardBody>
    </Card>
  )
}
